
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { ArchiveFile, SearchResult, DBRow } from '../types';

interface ReadingsBrowserProps {
    allFiles: ArchiveFile[];
    localDB: React.MutableRefObject<Map<string, DBRow>>;
    isIndexing: boolean;
    indexedCount: number;
    memoryUsageMB: number;
    startIndexing: () => Promise<void>;
    isLoading: boolean;
    onFileSelect: (file: ArchiveFile) => void;
    searchTerm: string;
    activeCategory: string;
}

const ReadingsBrowser: React.FC<ReadingsBrowserProps> = ({ 
    allFiles,
    localDB,
    isIndexing,
    indexedCount,
    memoryUsageMB,
    isLoading: isLoadingFiles,
    onFileSelect,
    searchTerm,
    activeCategory
}) => {
  const [searchResults, setSearchResults] = useState<SearchResult[] | null>(null);

  const filesForCategory = useMemo(() => {
    if (activeCategory === 'all') return [];
    return allFiles.filter(f => f.category === activeCategory);
  }, [allFiles, activeCategory]);
  
  useEffect(() => {
    if (!searchTerm.trim()) {
        setSearchResults(null);
        return;
    }
    const fetchRemoteSearch = async () => {
        try {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Zurich';
            const res = await fetch(`/api/indexer/search?q=${encodeURIComponent(searchTerm)}&timeZone=${encodeURIComponent(tz)}`);
            if (res.ok) {
                const data = await res.json();
                const hits = Array.isArray(data) ? data : (data.hits || data.results || []);
                const results: SearchResult[] = hits.map((hit: any) => {
                    const doc = hit.document || hit;
                    return {
                        file: {
                            id: doc.path || doc.name || doc.id || '',
                            name: doc.name || doc.path || doc.id || 'Reading',
                            type: 'html',
                            path: doc.path || doc.name || doc.id || '',
                            category: doc.category || 'Readings',
                            lastModified: 'Vertex AI Search Record',
                            description: doc.snippet || doc.description || ''
                        },
                        relevanceScore: hit.score ? Math.round(hit.score * 100) : 100,
                        snippet: doc.snippet || doc.content?.substring(0, 150) + '...' || '',
                        displayUrl: doc.path || doc.id || '',
                        aiInsights: 'Vertex AI Search'
                    };
                });
                setSearchResults(results);
            } else {
                setSearchResults([]);
            }
        } catch (e) {
            console.error("Remote search error:", e);
            setSearchResults([]);
        }
    };
    const timer = setTimeout(fetchRemoteSearch, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  return (
    <div className="flex flex-col h-full w-full bg-[#fbfaf6] dark:bg-stone-900 transition-colors duration-300">
        <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar bg-[#f2efe4]/30 dark:bg-stone-900/30">
            {searchTerm && searchResults ? (
                <div className="max-w-4xl mx-auto p-6 space-y-4 pb-20">
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] text-stone-400 font-bold uppercase tracking-[0.2em]">{searchResults.length} matches found</p>
                        {isIndexing && <span className="text-[10px] text-amber-500 font-bold animate-pulse">Scanning archives...</span>}
                    </div>
                    {searchResults.length > 0 ? searchResults.map((res, i) => (
                        <div key={res.file.id} onClick={() => onFileSelect(res.file)} className="p-6 bg-white dark:bg-stone-800 rounded-2xl border border-stone-200 dark:border-stone-700 shadow-sm cursor-pointer hover:border-amber-500 hover:shadow-md transition-all group animate-in fade-in slide-in-from-bottom-2 duration-300" style={{ animationDelay: `${Math.min(i * 50, 500)}ms` }}>
                            <div className="flex justify-between items-start mb-2">
                                <h3 className="font-serif font-bold text-xl text-stone-800 dark:text-stone-100 group-hover:text-amber-600 transition-colors">{res.file.name}</h3>
                                <span className="px-2 py-0.5 bg-stone-100 dark:bg-stone-700 text-[9px] font-bold text-stone-500 uppercase rounded">{res.file.category}</span>
                            </div>
                            <p className="text-xs text-stone-400 mb-3 flex items-center gap-2 font-mono">
                                <i className="fa-solid fa-link text-[10px]"></i>
                                {res.file.path}
                            </p>
                            <div className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed italic relative">
                                <span className="text-amber-500 mr-1">"</span>
                                {res.snippet}
                                <span className="text-amber-500 ml-1">"</span>
                            </div>
                        </div>
                    )) : (
                        <div className="py-20 text-center space-y-4">
                            <i className="fa-solid fa-magnifying-glass-blur text-4xl text-stone-200"></i>
                            <p className="text-stone-500">No matches found in currently indexed records.</p>
                            {indexedCount < allFiles.length && (
                                <p className="text-xs text-stone-400">Background indexing is still active...</p>
                            )}
                        </div>
                    )}
                </div>
            ) : activeCategory !== 'all' ? (
                 <div className="max-w-4xl mx-auto space-y-4 pb-20">
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] text-stone-400 font-bold uppercase tracking-[0.2em]">{filesForCategory.length} records in this category</p>
                    </div>
                    {filesForCategory.length > 0 ? filesForCategory.map((file, i) => (
                        <div key={file.id} onClick={() => onFileSelect(file)} className="p-6 bg-white dark:bg-stone-800 rounded-2xl border border-stone-200 dark:border-stone-700 shadow-sm cursor-pointer hover:border-amber-500 hover:shadow-md transition-all group animate-in fade-in slide-in-from-bottom-2 duration-300" style={{ animationDelay: `${Math.min(i * 50, 500)}ms` }}>
                            <h3 className="font-serif font-bold text-xl text-stone-800 dark:text-stone-100 group-hover:text-amber-600 transition-colors mb-2">{file.name}</h3>
                            <p className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed line-clamp-2">
                                {file.description || "No description available."}
                            </p>
                        </div>
                    )) : (
                        <div className="py-20 text-center space-y-4">
                            <i className="fa-solid fa-folder-open text-4xl text-stone-200"></i>
                            <p className="text-stone-500">No records found in this category.</p>
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center h-full text-stone-400 text-center animate-in fade-in duration-700">
                    <div className="w-24 h-24 mx-auto mb-8 rounded-3xl bg-white dark:bg-stone-800 flex items-center justify-center border border-stone-200 dark:border-stone-700 shadow-xl group hover:scale-105 transition-transform">
                        <i className={`fa-solid ${isIndexing ? 'fa-sync animate-spin' : 'fa-database'} text-4xl text-stone-300 group-hover:text-amber-500 transition-colors`}></i>
                    </div>
                    <h2 className="text-2xl font-serif text-stone-700 dark:text-stone-200 mb-4">Readings Archive Explorer</h2>
                </div>
            )}
        </div>
    </div>
  );
};

export default ReadingsBrowser;
